import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const FORM_URL = 'https://readdy.ai/api/form/d9jnohmc26n1c7c5q860';
const PDF_URL = `${import.meta.env.BASE_URL}report-acquirenti-stranieri-marche.pdf`;

const STATS = [
  { value: '€180K', label: 'Budget medio acquirente estero nelle Marche' },
  { value: '34%', label: 'degli acquirenti stranieri cerca borghi storici' },
  { value: '12+', label: 'nazionalità rappresentate nei dati Gate-away.com' },
  { value: '2028', label: 'Ancona Capitale Europea della Cultura' },
];

const ReportSection = () => {
  const left = useScrollAnimation({ threshold: 0.1 });
  const right = useScrollAnimation({ threshold: 0.15 });

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(responseText); } catch { /* non-JSON */ }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '';
      if (res.ok && parsed?.code === 'OK') {
        const link = document.createElement('a');
        link.href = PDF_URL;
        link.download = 'report-acquirenti-stranieri-marche.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setStatus('success');
      } else if (serverMsg.toLowerCase().includes('spam')) {
        setStatus('error');
        setFormError('Si è verificato un problema con l\'invio. Riprova tra qualche istante.');
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Si è verificato un errore. Riprova.');
      }
    } catch {
      setStatus('error');
      setFormError('Errore di connessione. Controlla la rete e riprova.');
    }
  };

  const inputClass =
    'w-full font-sans text-sm bg-ivory-100 border-0 rounded-lg px-6 py-4 text-anthracite-700 placeholder-anthracite-300 focus:outline-none focus:ring-2 focus:ring-terracotta-200 transition-all duration-300';

  return (
    <section
      id="report-acquirenti"
      className="bg-white py-24 md:py-32 px-6"
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

          {/* Left: content */}
          <div
            ref={left.ref}
            className={`fade-in-up ${left.isVisible ? 'visible' : ''}`}
          >
            <p className="font-sans text-xs tracking-widest2 uppercase text-terracotta-300 mb-5">
              Report gratuito · Gate-away.com
            </p>

            <h2 className="font-serif text-4xl md:text-5xl lg:text-5xl text-anthracite-800 font-light leading-tight mb-8">
              Vuoi proporre il tuo immobile anche ad una{' '}
              <em className="cormorant-italic not-italic text-terracotta-300">
                clientela internazionale?
              </em>
            </h2>

            <div className="flex flex-col gap-5 mb-10">
              <p className="font-sans text-base md:text-lg leading-loose text-anthracite-500">
                Prima conosci le loro preferenze in termini immobiliari!<br /><br />
                Scarica subito il PDF che ho creato per te sugli ultimi dati diffusi da{' '}
                <strong className="text-anthracite-700 font-semibold">Gate-away</strong>,
                il portale immobiliare internazionale!
              </p>
              <p className="font-sans text-base md:text-lg leading-loose text-anthracite-500">
                <strong className="text-anthracite-700 font-semibold">Ancona Capitale della Cultura nel 2028</strong>{' '}
                e i teatri marchigiani promossi a{' '}
                <strong className="text-anthracite-700 font-semibold">Patrimonio UNESCO</strong>:
                i riflettori del mondo intero puntano verso le Marche!<br /><br />
                Non facciamoci trovare impreparati.
              </p>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-4">
              {STATS.map((stat, i) => (
                <div
                  key={i}
                  className="rounded-lg p-5 bg-ivory-100 border border-ivory-200"
                >
                  <p className="font-serif text-3xl font-bold mb-1 text-terracotta-300">
                    {stat.value}
                  </p>
                  <p className="font-sans text-xs font-medium leading-relaxed text-anthracite-500">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div
            ref={right.ref}
            className={`fade-in-up stagger-2 ${right.isVisible ? 'visible' : ''}`}
          >
            <div className="rounded-2xl p-8 md:p-10 bg-ivory-100 border border-ivory-200">

              {/* PDF icon + label */}
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-terracotta-50 border border-terracotta-100">
                  <i className="ri-file-pdf-2-line text-xl text-terracotta-300"></i>
                </div>
                <div>
                  <p className="font-sans text-sm font-semibold text-anthracite-700">
                    Report acquirenti stranieri — Marche
                  </p>
                  <p className="font-sans text-xs text-anthracite-300">
                    Dati Gate-away.com · Download gratuito
                  </p>
                </div>
              </div>

              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-olive-50 border border-olive-200 mb-6">
                    <i className="ri-download-line text-2xl text-olive-400"></i>
                  </div>
                  <h3 className="font-serif text-2xl text-anthracite-800 mb-3">
                    Il download sta partendo!
                  </h3>
                  <p className="font-sans text-sm text-anthracite-500">
                    Se non parte automaticamente,{' '}
                    <a
                      href={PDF_URL}
                      download="report-acquirenti-stranieri-marche.pdf"
                      className="underline text-terracotta-300 hover:text-terracotta-400 cursor-pointer"
                    >
                      clicca qui
                    </a>
                    .
                  </p>
                  <button
                    onClick={() => { setStatus('idle'); setName(''); setEmail(''); }}
                    className="mt-6 font-sans text-xs text-anthracite-300 hover:text-terracotta-300 underline cursor-pointer transition-colors"
                  >
                    Scarica di nuovo
                  </button>
                </div>
              ) : (
                <>
                  <p className="font-sans text-base font-bold text-anthracite-700 mb-2">
                    Lascia la tua email, ti invio il report
                  </p>
                  <p className="font-sans text-xs text-anthracite-400 mb-7">
                    Nessuno spam. Solo il PDF e qualche aggiornamento utile sul mercato nelle Marche.
                  </p>

                  <form
                    onSubmit={handleSubmit}
                    id="report-acquirenti-marche"
                    className="flex flex-col gap-5"
                    data-readdy-form=""
                  >
                    {/* Honeypot */}
                    <input
                      type="text"
                      name="website_alt"
                      className="form-website-alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Il tuo nome"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={inputClass}
                    />
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="La tua email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputClass}
                    />

                    {status === 'error' && formError && (
                      <p className="font-sans text-sm text-terracotta-400 bg-terracotta-50 rounded-lg px-4 py-3">
                        {formError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full py-5 bg-terracotta-300 text-white font-sans text-sm tracking-widest uppercase rounded-full hover:bg-terracotta-400 transition-all duration-300 disabled:opacity-50 cursor-pointer whitespace-nowrap mt-2 font-semibold"
                    >
                      {status === 'loading' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Preparando il download...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-3">
                          Scarica il report gratuito
                          <i className="ri-download-line hidden sm:inline"></i>
                        </span>
                      )}
                    </button>

                    <p className="font-sans text-xs text-center text-anthracite-300">
                      Continuando accetti la nostra{' '}
                      <Link to="/privacy-policy" className="underline hover:text-terracotta-300 transition-colors cursor-pointer">
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  </form>
                </>
              )}
            </div>

            {/* Trust indicators */}
            <div className="flex items-center justify-center gap-6 mt-5">
              <div className="flex items-center gap-2">
                <i className="ri-lock-line text-sm text-anthracite-300"></i>
                <span className="font-sans text-xs text-anthracite-300">Nessuno spam</span>
              </div>
              <div className="w-px h-4 bg-ivory-200"></div>
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-sm text-anthracite-300"></i>
                <span className="font-sans text-xs text-anthracite-300">GDPR compliant</span>
              </div>
              <div className="w-px h-4 bg-ivory-200"></div>
              <div className="flex items-center gap-2">
                <i className="ri-file-pdf-2-line text-sm text-anthracite-300"></i>
                <span className="font-sans text-xs text-anthracite-300">100% gratuito</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ReportSection;